import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import type { AiJob, ResponseDetail } from '@grc/types';
import { apiFetch, ApiRequestError } from '../../../lib/api.js';
import { useOrg } from '../../../lib/auth.js';
import { Badge, Button, Field, Select, Textarea, useToast } from '../../../components/ui.js';
import { Markdown } from '../../../components/Markdown.js';

const KINDS = [
  { value: 'draft', label: 'Draft a response', hint: 'Uses your shared corpus as source material.' },
  { value: 'improve', label: 'Improve the current response', hint: 'Clearer, audit-ready wording.' },
  { value: 'gap_check', label: 'Find gaps', hint: 'What an assessor would still expect to see.' },
  { value: 'evidence_suggest', label: 'Suggest evidence', hint: 'Files worth attaching.' },
] as const;

export function AiAssist({
  response,
  onAccept,
}: {
  response: ResponseDetail;
  onAccept: (job: AiJob) => void;
}) {
  const { org } = useOrg();
  const { toast } = useToast();
  const [kind, setKind] = useState<(typeof KINDS)[number]['value']>('draft');
  const [instructions, setInstructions] = useState('');
  const [job, setJob] = useState<AiJob | null>(null);
  const [unavailable, setUnavailable] = useState(false);

  const canUse = org.role === 'admin' || org.role === 'contributor';
  const editable = !['finalised', 'assessed', 'locked'].includes(response.status);

  const generate = useMutation({
    mutationFn: () =>
      apiFetch<AiJob>('/ai/generate', {
        method: 'POST',
        body: { kind, responseId: response.id, instructions: instructions.trim() || undefined },
      }),
    onSuccess: (result) => setJob(result),
    onError: (err) => {
      if (err instanceof ApiRequestError && err.code === 'not_configured') {
        setUnavailable(true);
      } else {
        toast(err instanceof ApiRequestError ? err.message : 'AI generation failed', 'error');
      }
    },
  });

  const discard = useMutation({
    mutationFn: (jobId: string) =>
      apiFetch(`/ai/jobs/${jobId}/disposition`, { method: 'POST', body: { disposition: 'rejected' } }),
    onSuccess: () => setJob(null),
  });

  if (!canUse) {
    return (
      <p style={{ color: 'var(--color-text-muted)' }}>
        AI assistance is available to admins and contributors when drafting responses.
      </p>
    );
  }

  if (unavailable) {
    return (
      <p style={{ color: 'var(--color-text-muted)' }}>
        AI assistance is not configured on this environment. An administrator can enable it by
        setting the OpenAI API key.
      </p>
    );
  }

  return (
    <div>
      <p style={{ color: 'var(--color-text-muted)', fontSize: 'var(--text-xs)' }}>
        AI output is a starting point, never an answer of record: it is logged, marked, and only
        enters your response when you accept it (PLAN §11 guardrails).
      </p>
      <Field label="What would you like help with?">
        {(props) => (
          <Select {...props} value={kind} onChange={(e) => setKind(e.target.value as typeof kind)}>
            {KINDS.map((k) => (
              <option key={k.value} value={k.value}>
                {k.label}
              </option>
            ))}
          </Select>
        )}
      </Field>
      <Field label="Extra instructions" hint={KINDS.find((k) => k.value === kind)?.hint}>
        {(props) => (
          <Textarea
            {...props}
            value={instructions}
            onChange={(e) => setInstructions(e.target.value)}
            style={{ minHeight: 64 }}
            placeholder="e.g. we use Microsoft 365 and Intune; keep it under 300 words"
          />
        )}
      </Field>
      <Button loading={generate.isPending} onClick={() => generate.mutate()} disabled={!editable && kind !== 'gap_check'}>
        Generate
      </Button>

      {job?.outputContent && (
        <div className="ai-output">
          <div className="ai-badge-row">
            <Badge tone="purple">AI suggestion</Badge>
            <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-faint)' }}>
              {job.model} · review before use
            </span>
          </div>
          <Markdown markdown={job.outputContent} />
          <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
            {editable && (kind === 'draft' || kind === 'improve') && (
              <Button size="sm" onClick={() => onAccept(job)}>
                Accept into editor
              </Button>
            )}
            <Button
              size="sm"
              variant="secondary"
              onClick={() => {
                void navigator.clipboard.writeText(job.outputContent ?? '');
                toast('Copied to clipboard.', 'success');
              }}
            >
              Copy
            </Button>
            <Button size="sm" variant="ghost" onClick={() => discard.mutate(job.id)}>
              Discard
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
